import { differenceBy } from 'lodash';
import {
  assoc,
  curry,
  dissoc,
  indexBy,
  insert,
  map,
  move,
  omit,
  pick,
  pipe,
  prop,
  remove,
} from 'ramda';

const moveWithinMetricSection = (sectionMetrics, setSectionMetrics, fromIndex, toIndex) =>
  setSectionMetrics(move(fromIndex, toIndex, sectionMetrics));

const moveToSelectedMetrics = ({
  selectedMetrics,
  setSelectedMetrics,
  availableMetrics,
  setAvailableMetrics,
  fromIndex,
  toIndex,
}) => {
  setSelectedMetrics(insert(toIndex, availableMetrics[fromIndex], selectedMetrics));
  setAvailableMetrics(remove(fromIndex, 1, availableMetrics));
};

const moveFromSelectedMetrics = ({
  selectedMetrics,
  setSelectedMetrics,
  availableMetrics,
  setAvailableMetrics,
  fromIndex,
  toIndex,
}) => {
  const metricToMove = { ...selectedMetrics[fromIndex], breakdownSelected: false };
  setAvailableMetrics(insert(toIndex, metricToMove, availableMetrics));
  setSelectedMetrics(remove(fromIndex, 1, selectedMetrics));
};

export const handleMetricDrop = ({
  selectedMetrics,
  setSelectedMetrics,
  availableMetrics,
  setAvailableMetrics,
}, dropResult) => {
  const fromIndex = dropResult.source.index;
  const toIndex = dropResult.destination.index;

  if (dropResult.source.droppableId === dropResult.destination.droppableId) {
    const sourceArray =
      dropResult.source.droppableId === 'selectedMetrics' ? selectedMetrics : availableMetrics;
    const setSourceArray =
      dropResult.source.droppableId === 'selectedMetrics' ? setSelectedMetrics : setAvailableMetrics;
    return moveWithinMetricSection(sourceArray, setSourceArray, fromIndex, toIndex);
  }

  if (dropResult.destination.droppableId === 'selectedMetrics') {
    return moveToSelectedMetrics({
      selectedMetrics,
      setSelectedMetrics,
      availableMetrics,
      setAvailableMetrics,
      fromIndex,
      toIndex,
    });
  }

  return moveFromSelectedMetrics({
    selectedMetrics,
    setSelectedMetrics,
    availableMetrics,
    setAvailableMetrics,
    fromIndex,
    toIndex,
  });
};

export const handleSelectMetric = ({
  selectedMetrics,
  setSelectedMetrics,
  availableMetrics,
  setAvailableMetrics,
}, metricId) => (
  moveToSelectedMetrics({
    selectedMetrics,
    setSelectedMetrics,
    availableMetrics,
    setAvailableMetrics,
    fromIndex: availableMetrics.findIndex(metric => metric.metricId === metricId),
    toIndex: selectedMetrics.length,
  })
);

export const handleDeselectMetric = ({
  selectedMetrics,
  setSelectedMetrics,
  availableMetrics,
  setAvailableMetrics,
}, metricId) => (
  moveFromSelectedMetrics({
    selectedMetrics,
    setSelectedMetrics,
    availableMetrics,
    setAvailableMetrics,
    fromIndex: selectedMetrics.findIndex(metric => metric.metricId === metricId),
    toIndex: availableMetrics.length,
  })
);

export const handleSelectAll = ({
  selectedMetrics,
  setSelectedMetrics,
  availableMetrics,
  setAvailableMetrics,
}) => {
  setSelectedMetrics([...selectedMetrics, ...availableMetrics]);
  setAvailableMetrics([]);
};

export const handleDeselectAll = ({
  selectedMetrics,
  setSelectedMetrics,
  availableMetrics,
  setAvailableMetrics,
}) => {
  setAvailableMetrics([
    ...availableMetrics,
    ...map(omit(['breakdownSelected']), selectedMetrics),
  ]);
  setSelectedMetrics([]);
};


const renameKey = curry((oldKey, newKey, objList) =>
  map(obj => assoc(newKey, prop(oldKey, obj), dissoc(oldKey, obj)), objList));

export const getInitialValues = (reportDefinition) => {
  if (!reportDefinition) {
    return {};
  }

  return pick(['name', 'type'], reportDefinition);
};

export const getSelectedMetrics = (metricsDefinitions, reportDefinition) => {
  if (!reportDefinition) {
    return [];
  }

  const definitionsById = pipe(
    indexBy(prop('id')),
    map(pick(['displayLabel', 'allowBreakdown', 'breakdownLabel'])),
  )(metricsDefinitions);

  return reportDefinition.reportMetrics.map(metric => ({
    ...metric,
    ...definitionsById[metric.metricId],
  }));
};

export const getAvailableMetrics = (metricsDefinitions, reportDefinition) => {
  const availableMetrics = renameKey('id', 'metricId', metricsDefinitions);
  if (reportDefinition) {
    return differenceBy(availableMetrics, reportDefinition.reportMetrics, 'metricId');
  }

  return availableMetrics;
};
